const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function formatFileSize(size: number | null | undefined): string {
  if (size === null || size === undefined) return '—';
  if (size < 1024) return `${size} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = size / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
}

// `formatRelativeTime` and `formatExactTime` moved to `shared/utils/time` when
// the secrets page became their third consumer. Re-exported here so this
// feature's own modules keep importing from one place, and so there stays
// exactly one implementation of "how this product writes a moment in time".
export { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

/**
 * The day a change happened, as a reader would name it.
 *
 * A log is read as "what happened today, what happened before that" — the
 * calendar date only becomes the answer once the day has no name of its own.
 */
export function formatDayLabel(at: string): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return 'Unknown date';

  const startOfDay = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const daysAgo = Math.round((startOfDay(new Date()) - startOfDay(date)) / DAY);

  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Groups newest-first events into the days they happened on, order preserved. */
export function groupByDay<T extends { occurred_at: string }>(
  events: T[],
): { day: string; events: T[] }[] {
  const groups: { day: string; events: T[] }[] = [];
  for (const event of events) {
    const day = formatDayLabel(event.occurred_at);
    const current = groups[groups.length - 1];
    if (current?.day === day) {
      current.events.push(event);
    } else {
      groups.push({ day, events: [event] });
    }
  }
  return groups;
}
